import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import {
  HiClipboardDocumentCheck,
  HiChartPie,
  HiTableCells,
  HiArrowTrendingUp,
} from 'react-icons/hi2';
import HealthScoreCard from './HealthScoreCard';
import {
  ASSESSMENT_CATEGORY_LABELS,
  computeChecklistCompletion,
  getEffectiveDiagnosticResponse,
} from './healthCheckHelpers';

function assessmentProgress(formData) {
  if (!formData || typeof formData !== 'object') return { answered: 0, total: 0, percent: 0 };
  const entries = Object.values(formData);
  const total = entries.length;
  const answered = entries.filter(e => e && e.status && e.status !== '').length;
  return {
    answered,
    total,
    percent: total > 0 ? Math.round((answered / total) * 100) : 0,
  };
}

export default function HealthOverviewSection({
  displayHealthData,
  assessmentData,
  checklistData,
  onTabAssessment,
  onTabChecklist,
  HealthScoreCardInnerProps = {},
}) {
  const checklistStats = useMemo(
    () => computeChecklistCompletion(checklistData || {}),
    [checklistData]
  );
  const progress = useMemo(
    () => assessmentProgress(assessmentData?.formData),
    [assessmentData?.formData]
  );

  const barData = useMemo(() => {
    const scores = assessmentData?.categoryScores || {};
    return Object.entries(ASSESSMENT_CATEGORY_LABELS).map(([key, name]) => ({
      key,
      name: name.replace(' & ', '\n& '),
      short: name.split(' ')[0],
      score: Number(scores[key]) || 0,
    }));
  }, [assessmentData?.categoryScores]);

  const radarData = useMemo(
    () =>
      barData.map(({ short, score }) => ({
        subject: short.length > 10 ? `${short.slice(0, 9)}…` : short,
        score,
        fullMark: 100,
      })),
    [barData]
  );

  const checklistRows = useMemo(() => {
    const keys = ['finance', 'sales', 'operations', 'hr', 'compliance'];
    const labels = {
      finance: 'Financial Health',
      sales: 'Sales & Marketing',
      operations: 'Operations',
      hr: 'Human Resources',
      compliance: 'Compliance & Risk',
    };
    return keys.map(k => {
      const items = Array.isArray(checklistData?.[k]) ? checklistData[k] : [];
      const done = items.filter(i => getEffectiveDiagnosticResponse(i) === 'yes').length;
      const n = items.length;
      return {
        key: k,
        label: labels[k],
        done,
        total: n,
        pct: n > 0 ? Math.round((done / n) * 100) : 0,
      };
    });
  }, [checklistData]);

  const overallAssessment = assessmentData?.overallScore ?? progress.percent;
  const labelClass = v =>
    v >= 80 ? 'text-emerald-700' : v >= 60 ? 'text-amber-700' : 'text-red-700';

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-5 shadow-sm"
        >
          <div className="flex items-center gap-2 text-blue-800">
            <HiChartPie className="h-6 w-6" />
            <span className="text-sm font-semibold">Health assessment</span>
          </div>
          <p className={`mt-3 text-3xl font-bold ${labelClass(Number(overallAssessment) || 0)}`}>
            {assessmentData?.overallScore != null
              ? `${assessmentData.overallScore}%`
              : progress.total > 0
                ? `${progress.percent}% (in progress)`
                : '—'}
          </p>
          <p className="mt-1 text-xs text-gray-600">
            {progress.total > 0
              ? `${progress.answered} / ${progress.total} questions answered (${progress.percent}%)`
              : 'Open the Health Assessment tab to begin'}
          </p>
          <button
            type="button"
            onClick={onTabAssessment}
            className="mt-3 text-sm font-medium text-blue-700 underline hover:text-blue-900"
          >
            View assessment
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-5 shadow-sm"
        >
          <div className="flex items-center gap-2 text-emerald-800">
            <HiClipboardDocumentCheck className="h-6 w-6" />
            <span className="text-sm font-semibold">Diagnostic checklist</span>
          </div>
          <p className={`mt-3 text-3xl font-bold ${labelClass(checklistStats.percent)}`}>
            {checklistStats.total > 0 ? `${checklistStats.percent}%` : '—'}
          </p>
          <p className="mt-1 text-xs text-gray-600">
            {checklistStats.total > 0
              ? `${checklistStats.done} / ${checklistStats.total} items completed`
              : 'No checklist items yet'}
          </p>
          <button
            type="button"
            onClick={onTabChecklist}
            className="mt-3 text-sm font-medium text-emerald-800 underline hover:text-emerald-950"
          >
            Open checklist
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 p-5 shadow-sm"
        >
          <div className="flex items-center gap-2 text-violet-800">
            <HiTableCells className="h-6 w-6" />
            <span className="text-sm font-semibold">Assessment coverage</span>
          </div>
          <p className="mt-3 text-3xl font-bold text-violet-900">{progress.percent}%</p>
          <p className="mt-1 text-xs text-gray-600">Form completion (all sections)</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5 shadow-sm"
        >
          <div className="flex items-center gap-2 text-amber-900">
            <HiArrowTrendingUp className="h-6 w-6" />
            <span className="text-sm font-semibold">Last saved</span>
          </div>
          <p className="mt-3 text-lg font-semibold text-gray-900">
            {assessmentData?.lastUpdated
              ? new Date(assessmentData.lastUpdated).toLocaleString()
              : displayHealthData?.lastUpdated
                ? typeof displayHealthData.lastUpdated === 'string'
                  ? new Date(displayHealthData.lastUpdated).toLocaleString()
                  : String(displayHealthData.lastUpdated)
                : '—'}
          </p>
          <p className="mt-1 text-xs text-gray-600">Assessment / health sync</p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
        >
          <h3 className="mb-4 text-lg font-bold text-gray-900">Scores by category</h3>
          {barData.some(d => d.score > 0) ? (
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="short" tick={{ fontSize: 11 }} interval={0} angle={-20} dy={8} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={32} />
                  <Tooltip
                    formatter={v => [`${v}%`, 'Score']}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.name?.replace('\n& ', ' & ') || ''
                    }
                  />
                  <Bar dataKey="score" fill="#2563eb" name="Score %" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="py-12 text-center text-sm text-gray-500">
              Save a health assessment to see category scores here.
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
        >
          <h3 className="mb-4 text-lg font-bold text-gray-900">Radar overview</h3>
          {barData.some(d => d.score > 0) ? (
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#7c3aed"
                    fill="#8b5cf6"
                    fillOpacity={0.45}
                  />
                  <Tooltip formatter={v => [`${v}%`, 'Score']} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="py-12 text-center text-sm text-gray-500">
              Category scores appear after you complete the assessment.
            </p>
          )}
        </motion.div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
        >
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
            <h3 className="font-bold text-gray-900">Assessment detail</h3>
            <p className="text-xs text-gray-600">Saved scores per section</p>
          </div>
          <div className="max-h-80 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b text-left text-gray-600">
                  <th className="px-4 py-2 font-semibold">Category</th>
                  <th className="px-4 py-2 font-semibold">Score</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {barData.map(row => (
                  <tr key={row.key} className="border-b border-gray-50 hover:bg-gray-50/80">
                    <td className="px-4 py-2 text-gray-900">{row.name.replace('\n& ', ' & ')}</td>
                    <td className="px-4 py-2 font-medium text-gray-800">{row.score}%</td>
                    <td className="px-4 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          row.score >= 80
                            ? 'bg-emerald-100 text-emerald-800'
                            : row.score >= 60
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {row.score >= 80 ? 'Strong' : row.score >= 60 ? 'OK' : 'Needs work'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
        >
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
            <h3 className="font-bold text-gray-900">Checklist by domain</h3>
            <p className="text-xs text-gray-600">Completion vs total items</p>
          </div>
          <div className="max-h-80 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b text-left text-gray-600">
                  <th className="px-4 py-2 font-semibold">Domain</th>
                  <th className="px-4 py-2 font-semibold">Done</th>
                  <th className="px-4 py-2 font-semibold">Total</th>
                  <th className="px-4 py-2 font-semibold">%</th>
                </tr>
              </thead>
              <tbody>
                {checklistRows.map(row => (
                  <tr key={row.key} className="border-b border-gray-50 hover:bg-gray-50/80">
                    <td className="px-4 py-2 text-gray-900">{row.label}</td>
                    <td className="px-4 py-2">{row.done}</td>
                    <td className="px-4 py-2">{row.total}</td>
                    <td className="px-4 py-2 font-medium">{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      <HealthScoreCard
        healthData={displayHealthData}
        onScoreClick={onTabAssessment}
        onDomainClick={onTabChecklist}
        showDetails={true}
        {...HealthScoreCardInnerProps}
      />
    </div>
  );
}
